/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { v4 as uuidv4 } from "uuid";

import { FormTemplate } from "../../../../../models/FormTemplate";
import { Paging } from "../../../../../models/paging";
import { Process } from "../../../../../models/Process";
import { NotificationService } from "../../../../../services/NotificationService";
import { SuperStoreService } from "../../../../../services/SuperStoreService";
import { calculatePageSize } from "../../../../../utils/listUtils";
import { formTemplateToProcess } from "../../../../../utils/templateUtils";

// Session storage keys
const SESSION_STORAGE_KEYS = {
  SELECTED_TEMPLATE_FILTERS: 'twp_selected_template_filters',
  COMPLETION_STATUS_FILTER: 'twp_completion_status_filter',
  COMPLETION_STATUS_FILTER_BY: 'twp_completion_status_filter_by'
};

// Helper functions for session persistence
const saveSelectedFiltersToSession = (filters: string[]) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.SELECTED_TEMPLATE_FILTERS, JSON.stringify(filters));
  } catch (error) {
    console.warn('Failed to save filters to session storage:', error);
  }
};

const saveCompletionStatusFilterToSession = (filter?: string, filterBy?: string) => {
  try {
    if (filter && filterBy) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER, filter);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER_BY, filterBy);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER);
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER_BY);
    }
  } catch (error) {
    console.warn('Failed to save completion status filter to session storage:', error);
  }
};

const loadSelectedFiltersFromSession = (): string[] => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEYS.SELECTED_TEMPLATE_FILTERS);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.warn('Failed to load filters from session storage:', error);
  }
  return [];
};

const loadCompletionStatusFilterFromSession = (): { filter?: string; filterBy?: string } => {
  try {
    const filter = sessionStorage.getItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER);
    const filterBy = sessionStorage.getItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER_BY);
    if (filter && filterBy) {
      return { filter, filterBy };
    }
  } catch (error) {
    console.warn('Failed to load completion status filter from session storage:', error);
  }
  return {};
};

const clearSelectedFiltersFromSession = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.SELECTED_TEMPLATE_FILTERS);
  } catch (error) {
    console.warn('Failed to clear filters from session storage:', error);
  }
};

const clearCompletionStatusFilterFromSession = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.COMPLETION_STATUS_FILTER_BY);
  } catch (error) {
    console.warn('Failed to clear completion status filter from session storage:', error);
  }
};

const clearAllFiltersFromSession = () => {
  clearSelectedFiltersFromSession();
  clearCompletionStatusFilterFromSession();
};

// Export session helper functions for use in other components
export { clearSelectedFiltersFromSession, clearAllFiltersFromSession };

export type TWPContextState = {
  processes: Process[];
  templates: FormTemplate[];
  paging: Paging;
  loading: boolean;
  error: string;
  pageNumber: number;
  totalCount: number;
  itemsPerPage: number;
  selectedTemplateFilters: string[]; // Track selected template filters
};

export type TWPContextAction = {
  type:
    | "PAGE_FORWARD"
    | "PAGE_BACKWARD"
    | "SET_PAGE"
    | "SET_PROCESSES"
    | "SET_ERROR"
    | "SET_LOADING"
    | "SET_TOTAL_COUNT"
    | "SET_TEMPLATES"
    | "SET_SELECTED_TEMPLATE_FILTERS"
    | "INITIALIZE_FROM_SESSION";
  payload?:
    | Process[]
    | Paging
    | string
    | boolean
    | number
    | FormTemplate[]
    | { name: string; templateId: string }
    | string[];
};

export type TWPContextType = {
  state: TWPContextState;
  dispatch: React.Dispatch<TWPContextAction>;
};

// Initialize state with session-stored filters
const getInitialState = (): TWPContextState => {
  const storedFilters = loadSelectedFiltersFromSession();
  const storedCompletionStatus = loadCompletionStatusFilterFromSession();
  
  return {
    processes: [],
    templates: [],
    paging: {
      take: calculatePageSize(),
      skip: 0,
      order: "desc",
      orderBy: "data.created",
      // Initialize with stored completion status filters
      filter: storedCompletionStatus.filter,
      filterBy: storedCompletionStatus.filterBy,
      // Don't set template filters here - we need templates list first to calculate included templates
      filter2: undefined,
      filterBy2: undefined,
    },
    loading: false,
    error: "",
    pageNumber: 0,
    totalCount: 0,
    itemsPerPage: calculatePageSize(),
    selectedTemplateFilters: storedFilters,
  };
};

export const TWPContext = React.createContext<TWPContextType>({
  state: getInitialState(),
  dispatch: () => {},
});

TWPContext.displayName = "TWPContext";

const initialState: TWPContextState = getInitialState();

export const useTWPContext = () => {

  const [state, dispatch] = React.useReducer(
    (state: TWPContextState, action: TWPContextAction) => {
      switch (action.type) {
        case "PAGE_FORWARD":
          if (state.paging.skip + state.paging.take < state.totalCount) {
            return {
              ...state,
              pageNumber: state.pageNumber + 1,
              paging: {
                ...state.paging,
                skip: state.paging.skip + state.paging.take,
              },
            };
          } else return state;
        case "PAGE_BACKWARD":
          if (state.paging.skip - state.paging.take >= 0) {
            return {
              ...state,
              pageNumber: state.pageNumber - 1,
              paging: {
                ...state.paging,
                skip: state.paging.skip - state.paging.take,
              },
            };
          } else return state;
        case "SET_PAGE":
          console.log(action.payload);
          const newPaging = action.payload as Paging;
          // Save completion status filters to session storage
          saveCompletionStatusFilterToSession(newPaging.filter, newPaging.filterBy);
          return {
            ...state,
            paging: newPaging,
          };
        case "SET_PROCESSES":
          return {
            ...state,
            processes: action.payload as Process[],
          };
        case "SET_ERROR":
          return {
            ...state,
            error: action.payload as string,
          };
        case "SET_LOADING":
          return {
            ...state,
            loading: action.payload as boolean,
          };
        case "SET_TOTAL_COUNT":
          return {
            ...state,
            totalCount: action.payload as number,
          };

        case "SET_TEMPLATES":
          const newTemplates = action.payload as FormTemplate[];
          
          // Always check session storage when templates are loaded, not just state.selectedTemplateFilters
          const currentStoredFilters = loadSelectedFiltersFromSession();
          const currentStoredCompletionStatus = loadCompletionStatusFilterFromSession();
          
          // If we have stored template filters, apply them now that templates are loaded
          if (currentStoredFilters.length > 0) {
            const allTemplateNames = newTemplates.map(t => t.data?.name || "").filter(name => name);
            const includedTemplates = allTemplateNames.filter(name => !currentStoredFilters.includes(name));
            
            return {
              ...state,
              templates: newTemplates,
              selectedTemplateFilters: currentStoredFilters, // Ensure state matches session storage
              paging: {
                ...state.paging,
                // Apply stored completion status filters
                filter: currentStoredCompletionStatus.filter,
                filterBy: currentStoredCompletionStatus.filterBy,
                // Apply template filters using filter2/filterBy2 with included templates
                filter2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? includedTemplates : undefined,
                filterBy2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? "data.template" : undefined,
              },
            };
          }
          
          // Even if no template filters, ensure completion status filters are applied from session
          return {
            ...state,
            templates: newTemplates,
            selectedTemplateFilters: currentStoredFilters, // Ensure state matches session storage
            paging: {
              ...state.paging,
              filter: currentStoredCompletionStatus.filter,
              filterBy: currentStoredCompletionStatus.filterBy,
            },
          };
        case "SET_SELECTED_TEMPLATE_FILTERS":
          const newFilters = action.payload as string[];
          // Save to session storage whenever filters change
          saveSelectedFiltersToSession(newFilters);
          return {
            ...state,
            selectedTemplateFilters: newFilters,
          };
        case "INITIALIZE_FROM_SESSION":
          // This action can be used to explicitly restore from session if needed
          const sessionFilters = loadSelectedFiltersFromSession();
          const sessionCompletionStatus = loadCompletionStatusFilterFromSession();
          
          console.log('INITIALIZE_FROM_SESSION called:', {
            sessionFilters,
            sessionCompletionStatus,
            currentSelectedFilters: state.selectedTemplateFilters,
            currentPagingFilter: state.paging.filter,
            templatesLoaded: state.templates.length
          });
          
          // If we have templates loaded, we can properly calculate included templates
          if (state.templates.length > 0 && sessionFilters.length > 0) {
            const allTemplateNames = state.templates.map(t => t.data?.name || "").filter(name => name);
            const includedTemplates = allTemplateNames.filter(name => !sessionFilters.includes(name));
            
            console.log('Restoring with templates loaded:', {
              allTemplateNames,
              excludedTemplates: sessionFilters,
              includedTemplates
            });
            
            return {
              ...state,
              selectedTemplateFilters: sessionFilters,
              paging: {
                ...state.paging,
                // Restore completion status filters
                filter: sessionCompletionStatus.filter,
                filterBy: sessionCompletionStatus.filterBy,
                // Apply template filters with correct included templates
                filter2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? includedTemplates : undefined,
                filterBy2: includedTemplates.length > 0 && includedTemplates.length < allTemplateNames.length ? "data.template" : undefined,
              },
            };
          }
          
          // If templates aren't loaded yet, just restore the selectedTemplateFilters and completion status
          console.log('Restoring without templates loaded yet');
          return {
            ...state,
            selectedTemplateFilters: sessionFilters,
            paging: {
              ...state.paging,
              filter: sessionCompletionStatus.filter,
              filterBy: sessionCompletionStatus.filterBy,
              // Don't set template filters until templates are loaded
              filter2: undefined,
              filterBy2: undefined,
            },
          };
        default:
          return state;
      }
    },
    initialState
  );

  // Fetch templates only once when component mounts
  React.useEffect(() => {
    console.log('Component mounted, fetching templates');
    fetchTemplates(dispatch);
  }, []);

  // Handle page visibility changes (like browser back button)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again, check if we need to restore session filters
        const storedFilters = loadSelectedFiltersFromSession();
        const storedCompletionStatus = loadCompletionStatusFilterFromSession();
        
        // Check if current state differs from stored state
        const currentFilters = state.selectedTemplateFilters;
        const currentFilter = state.paging.filter;
        const currentFilterBy = state.paging.filterBy;
        
        const filtersChanged = JSON.stringify(currentFilters.sort()) !== JSON.stringify(storedFilters.sort());
        const completionStatusChanged = currentFilter !== storedCompletionStatus.filter || currentFilterBy !== storedCompletionStatus.filterBy;
        
        if (filtersChanged || completionStatusChanged) {
          // State differs from session storage, restore from session
          dispatch({
            type: "INITIALIZE_FROM_SESSION",
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.selectedTemplateFilters, state.paging.filter, state.paging.filterBy, state.templates.length]);

  // Fetch processes and count when paging changes
  React.useEffect(() => {
    dispatch({
      type: "SET_LOADING",
      payload: true,
    });

    Promise.all([
      fetchProcesses(dispatch, state),
      fetchTotalCount(dispatch, state),
    ])
      .then(() => {
        dispatch({
          type: "SET_LOADING",
          payload: false,
        });
      })
      .catch(() => {
        dispatch({
          type: "SET_LOADING",
          payload: false,
        });
        dispatch({
          type: "SET_ERROR",
          payload: "Error fetching data",
        });
      });

    return () => {};
  }, [state.paging]);

  return {
    state,
    dispatch,
  };
};

const fetchProcesses = async (
  dispatch: React.Dispatch<TWPContextAction>,
  state: TWPContextState
) => {
  try {
    const _client = SuperStoreService.getInstance();
    const _processes = await _client.getItems<Process>(state.paging, "process");
    dispatch({
      type: "SET_PROCESSES",
      payload: _processes,
    });
  } catch (error) {}
};

const fetchTotalCount = async (
  dispatch: React.Dispatch<TWPContextAction>,
  state: TWPContextState
) => {
  try {
    const _client = SuperStoreService.getInstance();
    const _totalCount = await _client.getItemsCount("process", state.paging);
    dispatch({
      type: "SET_TOTAL_COUNT",
      payload: _totalCount,
    });
  } catch (error) {}
};

const fetchTemplates = async (dispatch: React.Dispatch<TWPContextAction>) => {
  try {
    const _client = SuperStoreService.getInstance();
    const _templates = await _client.getItems<FormTemplate>(
      { take: 1000, skip: 0 },
      "formtemplate"
    );
    dispatch({
      type: "SET_TEMPLATES",
      payload: _templates,
    });
  } catch (error) {}
};

export const createProcess = async (
  dispatch: React.Dispatch<TWPContextAction>,
  state: TWPContextState,
  payload: {
    name: string;
    templateId: string;
  }
): Promise<Process | null> => {
  console.log("payload:", payload);

  try {
    const _client = SuperStoreService.getInstance();
    const _template = await _client.getItem<FormTemplate>(
      "formtemplate",
      payload.templateId
    );

    const _process = formTemplateToProcess(_template!);

    if (_process && _process.data) {
      _process.data.name = payload.name;
      _process.id = uuidv4();
      _process.entityType = "process";
      await _client.upsertItem("process", _process, _process.id);

      await fetchProcesses(dispatch, state);
      await fetchTotalCount(dispatch, state);

      sendNewProcessNotification(_process);

      return _process;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
  return null;
};

export const sendNewProcessNotification = async (process: Process) => {
  try {

    const _notiticationService = NotificationService.getInstance();

    _notiticationService.sendEvent(`Ein neuer Prozess wurde gestartet: ${process.data.name}`);

  } catch (error) {
    console.log(error);
  }


}

export const deleteProcess = async (
  dispatch: React.Dispatch<TWPContextAction>,
  state: TWPContextState,
  id: string
) => {
  try {
    const _client = SuperStoreService.getInstance();
    await _client.deleteItem("process", id);
    await fetchProcesses(dispatch, state);
    await fetchTotalCount(dispatch, state);
  } catch (error) {
    console.log(error);
  }
};
